import { EOpenAIUserRole, IOpenAICodeGeneratorMessageDTO, IUserInfoDto } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import classNames from "classnames";
import csharp from "highlight.js/lib/languages/csharp";
import go from "highlight.js/lib/languages/go";
import java from "highlight.js/lib/languages/java";
import python from "highlight.js/lib/languages/python";
import rust from "highlight.js/lib/languages/rust";
import sql from "highlight.js/lib/languages/sql";
import typescript from "highlight.js/lib/languages/typescript";
import "highlight.js/styles/github.min.css";
import MarkedReact from "marked-react";
import { Component, KeyboardEvent, ReactNode, createRef } from "react";
import Lowlight from "react-lowlight";
import { OpenAIService } from "../../../services/openai-service";
import { KContext } from "../../../shared/hoc/with-context";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import SpinSvg from "../../../svgs/spin.svg";
import "./ai-code-generator.scss";
import KuikaAvatar, { getIdentitySeed } from "../../ui-kit/kuika-avatar/kuika-avatar";
import KuikaCopyButton from "../../ui-kit/kuika-copy-button/kuika-copy-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";

Lowlight.registerLanguage("csharp", csharp);
Lowlight.registerLanguage("typescript", typescript);
Lowlight.registerLanguage("python", python);
Lowlight.registerLanguage("java", java);
Lowlight.registerLanguage("go", go);
Lowlight.registerLanguage("rust", rust);
Lowlight.registerLanguage("sql", sql);

/** A message that is only a fenced block gets no grey bubble (Figma). */
const CODE_ONLY_MESSAGE: RegExp = /^\s*```[\s\S]*```\s*$/;

interface IAICodeCardProps {
  snippet: string;
  language: string;
  onApplyCode?: (code: string) => void;
}

/** Figma "AI Chat panel" code card. The old Apply/Copy buttons floated over the code and covered it. */
const AICodeCard = ({ snippet, language, onApplyCode }: IAICodeCardProps): JSX.Element => {
  return (
    <div className="kuika__ai-code-generator__code-card" data-testid="aiCodeGeneratorCodeBlock">
      <div className="kuika__ai-code-generator__code-card__header">
        <span className="kuika__ai-code-generator__code-card__lang">{language.toUpperCase()}</span>
        <span className="kuika__ai-code-generator__code-card__actions">
          {/* Tooltipped like its Apply sibling: both are 16px glyphs, and a glyph on its own says
              nothing until it is hovered. The ✓ the button rolls to is the copy's own answer. */}
          <KuikaTooltip content="Copy">
            <KuikaCopyButton
              value={snippet}
              size="xs"
              label="Copy"
              copiedLabel="Copied"
              data-testid="aiCodeGeneratorCopyButton"
            />
          </KuikaTooltip>
          {onApplyCode ? (
            <KuikaTooltip content="Apply to editor">
              <button
                type="button"
                className="kuika__ai-code-generator__code-card__action"
                onClick={() => onApplyCode(snippet)}
                data-testid="aiCodeGeneratorApplyButton"
              >
                <KuikaSvg type="sidebar-icons" name="file-plus" width={12} height={12} />
              </button>
            </KuikaTooltip>
          ) : null}
        </span>
      </div>
      <div className="kuika__ai-code-generator__code-card__body">
        <Lowlight language={language} value={snippet} markers={[]} />
      </div>
    </div>
  );
};

export enum EAICodeGeneratorType {
  CSharp = "csharp",
  TypeScript = "typescript",
  Python = "python",
  Java = "java",
  Go = "go",
  Rust = "rust",
  SQL = "sql",
  Oracle = "oracle",
  Postgre = "postgre"
}

const DEFAULT_FENCE_LANGUAGE: Record<EAICodeGeneratorType, string> = {
  [EAICodeGeneratorType.CSharp]: "csharp",
  [EAICodeGeneratorType.TypeScript]: "typescript",
  [EAICodeGeneratorType.Python]: "python",
  [EAICodeGeneratorType.Java]: "java",
  [EAICodeGeneratorType.Go]: "go",
  [EAICodeGeneratorType.Rust]: "rust",
  [EAICodeGeneratorType.SQL]: "sql",
  [EAICodeGeneratorType.Oracle]: "sql",
  [EAICodeGeneratorType.Postgre]: "sql"
};

export interface IAICodeGeneratorState {
  messages: IOpenAICodeGeneratorMessageDTO[];
  isLoading: boolean;
  /** Selected table for SQL types; until one is picked, a hint replaces the prompt row. */
  selectedTable?: string;
  /**
   * An example prompt the user picked before choosing a table. Held as the prompt's
   * placeholder — grey, not typed text — and sent as soon as a table is chosen.
   */
  pendingSuggestion?: string;
}

/** Example prompt chips in the empty chat. */
export interface IAICodeGeneratorSuggestion {
  prompt: string;
  /** File name under public/images/common. */
  icon: string;
}

export interface IAICodeGeneratorProps {
  type: EAICodeGeneratorType;
  tables?: string[];
  datasourceId?: string;
  /** When set, the empty chat shows these instead of initMessage. */
  suggestions?: IAICodeGeneratorSuggestion[];
  /** When set, code blocks get an Apply button that writes into the caller's editor. */
  onApplyCode?: (code: string) => void;
}

export class AICodeGenerator extends Component<IAICodeGeneratorProps, IAICodeGeneratorState> {
  formRef = createRef<FormInstance<any>>();

  messagesContainerRef = createRef<HTMLDivElement>();

  // Reads window.userInfo. Not React's legacy this.context.
  kContext: KContext = new KContext();

  constructor(props) {
    super(props);
    this.state = {
      messages: [],
      isLoading: false
    };
  }

  handleSendCodeGenerateRequest = (data) => {
    this.formRef.current?.setFieldsValue({ ...data, userMessage: "" });

    this.handlePushMessage({ role: EOpenAIUserRole.User, content: data.userMessage });

    if (this.props.type === EAICodeGeneratorType.CSharp) {
      this.handleSendCSCodeGenerateRequest(this.state.messages);
    } else if (this.props.type === EAICodeGeneratorType.TypeScript) {
      this.handleSendTSCodeGenerateRequest(this.state.messages);
    } else if (this.props.type === EAICodeGeneratorType.Python) {
      this.handleSendPythonCodeGenerateRequest(this.state.messages);
    } else if (this.props.type === EAICodeGeneratorType.Java) {
      this.handleSendJavaCodeGenerateRequest(this.state.messages);
    } else if (this.props.type === EAICodeGeneratorType.Go) {
      this.handleSendGoCodeGenerateRequest(this.state.messages);
    } else if (this.props.type === EAICodeGeneratorType.Rust) {
      this.handleSendRustCodeGenerateRequest(this.state.messages);
    } else {
      this.handleSendSQLCodeGenerateRequest(data, this.state.messages);
    }
  };

  handleSendPythonCodeGenerateRequest = (messages: IOpenAICodeGeneratorMessageDTO[]) => {
    this.setState({ isLoading: true });
    OpenAIService.GeneratePython({ messages })
      .then(({ data }) => {
        this.handlePushMessage(data.responseMessage);
      })
      .catch((error) => {
        KMainFunctions.exceptionHandler(error);
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  handleSendJavaCodeGenerateRequest = (messages: IOpenAICodeGeneratorMessageDTO[]) => {
    this.setState({ isLoading: true });
    OpenAIService.GenerateJava({ messages })
      .then(({ data }) => {
        this.handlePushMessage(data.responseMessage);
      })
      .catch((error) => {
        KMainFunctions.exceptionHandler(error);
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  handleSendGoCodeGenerateRequest = (messages: IOpenAICodeGeneratorMessageDTO[]) => {
    this.setState({ isLoading: true });
    OpenAIService.GenerateGo({ messages })
      .then(({ data }) => {
        this.handlePushMessage(data.responseMessage);
      })
      .catch((error) => {
        KMainFunctions.exceptionHandler(error);
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  handleSendRustCodeGenerateRequest = (messages: IOpenAICodeGeneratorMessageDTO[]) => {
    this.setState({ isLoading: true });
    OpenAIService.GenerateRust({ messages })
      .then(({ data }) => {
        this.handlePushMessage(data.responseMessage);
      })
      .catch((error) => {
        KMainFunctions.exceptionHandler(error);
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  handleSendCSCodeGenerateRequest = (messages: IOpenAICodeGeneratorMessageDTO[]) => {
    this.setState({ isLoading: true });
    OpenAIService.GenerateCSharp({ messages })
      .then(({ data }) => {
        this.handlePushMessage(data.responseMessage);
      })
      .catch((error) => {
        KMainFunctions.exceptionHandler(error);
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  handleSendTSCodeGenerateRequest = (messages: IOpenAICodeGeneratorMessageDTO[]) => {
    this.setState({ isLoading: true });
    OpenAIService.GenerateTypeScript({ messages })
      .then(({ data }) => {
        this.handlePushMessage(data.responseMessage);
      })
      .catch((error) => {
        KMainFunctions.exceptionHandler(error);
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  handleSendSQLCodeGenerateRequest = (formValues, messages: IOpenAICodeGeneratorMessageDTO[]) => {
    this.setState({ isLoading: true });
    var datasourceId = this.props.datasourceId;
    OpenAIService.GenerateSql({
      messages,
      importantTableName: formValues.dataTable,
      newSession: messages.length === 1,
      datasourceId
    })
      .then(({ data }) => {
        this.handlePushMessage(data.responseMessage);
      })
      .catch((error) => {
        KMainFunctions.exceptionHandler(error);
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  handleSuggestionClick = (prompt: string): void => {
    // No table yet: keep the example as a placeholder instead of dropping it into the box
    // as text the user did not type. Choosing a table sends it.
    if (this.needsTableSelection()) {
      this.setState({ pendingSuggestion: prompt });
      return;
    }

    this.formRef.current?.setFieldsValue({ userMessage: prompt });
    // Submitting lets the form validate required fields such as the table choice.
    this.formRef.current?.submit();
  };

  /** SQL dialects cannot answer before a table is picked. */
  needsTableSelection = (): boolean => {
    const { type, tables } = this.props;
    const isSqlDialect: boolean =
      type === EAICodeGeneratorType.SQL ||
      type === EAICodeGeneratorType.Oracle ||
      type === EAICodeGeneratorType.Postgre;
    return isSqlDialect && !!tables && !this.state.selectedTable;
  };

  handleTableSelected = (table: string): void => {
    const pending: string | undefined = this.state.pendingSuggestion;
    this.setState({ selectedTable: table, pendingSuggestion: undefined }, () => {
      if (!pending) return;
      this.formRef.current?.setFieldsValue({ userMessage: pending });
      this.formRef.current?.submit();
    });
  };

  renderEmptyState = (): JSX.Element => {
    const suggestions: IAICodeGeneratorSuggestion[] = this.props.suggestions ?? [];
    return (
      <div className="kuika__ai-code-generator__empty-state" data-testid="aiCodeGeneratorEmptyState">
        <KuikaSvg
          name="ai-sparkle"
          width={34}
          height={34}
          useOriginalColors
          className="kuika__ai-code-generator__empty-state__avatar"
        />
        <div className="kuika__ai-code-generator__empty-state__title">Get Started with AI</div>
        <div className="kuika__ai-code-generator__empty-state__description">
          Choose an example prompt or type your own question.
        </div>
        <div className="kuika__ai-code-generator__empty-state__suggestions">
          {suggestions.map((suggestion: IAICodeGeneratorSuggestion) => (
            <button
              key={suggestion.prompt}
              type="button"
              className="kuika__ai-code-generator__empty-state__suggestion"
              onClick={() => this.handleSuggestionClick(suggestion.prompt)}
              data-testid="aiCodeGeneratorSuggestion"
            >
              <KuikaSvg
                name={suggestion.icon}
                width={12}
                height={12}
                useOriginalColors
                className="kuika__ai-code-generator__empty-state__suggestion__icon"
              />
              <span>{suggestion.prompt}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  handleInputOnEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.code !== "Enter" || this.state.isLoading) return;
    e.preventDefault();
    this.formRef.current?.submit();
  };

  handlePushMessage = (message: IOpenAICodeGeneratorMessageDTO) => {
    const messages = this.state.messages;
    messages.push(message);
    this.setState({ messages }, () => {
      const lastMessage =
        this.messagesContainerRef.current?.children[this.messagesContainerRef.current.children.length - 1];

      lastMessage?.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
    });
  };

  renderMessage = (message: IOpenAICodeGeneratorMessageDTO): JSX.Element => {
    const isAssistant: boolean = message.role === EOpenAIUserRole.Assistant;
    const messageTestId: string = isAssistant ? "aiCodeGeneratorAiMessage" : "aiCodeGeneratorUserMessage";

    // Code-only answers drop the bubble so the card stands alone (Figma).
    const isCodeOnly: boolean = isAssistant && CODE_ONLY_MESSAGE.test(message.content ?? "");

    const body: JSX.Element = (
      <div
        className={classNames("kuika__ai-code-generator__bubble", {
          "kuika__ai-code-generator__bubble--bare": isCodeOnly
        })}
        data-testid={messageTestId}
      >
        <MarkedReact
          value={message.content}
          renderer={{
            code: (snippet: ReactNode, lang: string | undefined) => {
              const language: string = lang || DEFAULT_FENCE_LANGUAGE[this.props.type] || "sql";
              const snippetStr: string = typeof snippet === "string" ? snippet : String(snippet ?? "");
              return <AICodeCard snippet={snippetStr} language={language} onApplyCode={this.props.onApplyCode} />;
            }
          }}
        />
      </div>
    );

    // Figma: user avatar on the right, assistant on the left.
    return (
      <div
        className={classNames("kuika__ai-code-generator__message", {
          "kuika__ai-code-generator__message--assistant": isAssistant
        })}
      >
        {isAssistant ? this.renderAssistantAvatar() : null}
        {body}
        {isAssistant ? null : this.renderUserAvatar()}
      </div>
    );
  };

  /** The asset already contains the disc and its border — do not rebuild them in CSS. */
  renderAssistantAvatar = (): JSX.Element => (
    <KuikaSvg name="ai-avatar" width={24} height={24} useOriginalColors className="kuika__ai-code-generator__avatar" />
  );

  /** Profile photo, falling back to initials — same source chain as user-menu-button. */
  renderUserAvatar = (): JSX.Element => {
    const userInfo: IUserInfoDto | undefined = this.kContext.userInfo;
    // KContext already reads window.profilePhoto.
    const photo: string = this.kContext.profilePhoto || userInfo?.profilePhotoUrl || "";
    return (
      <KuikaAvatar
        size={24}
        src={photo || undefined}
        alt={userInfo?.username ?? ""}
        seed={getIdentitySeed(userInfo)}
        className="kuika__ai-code-generator__avatar kuika__ai-code-generator__avatar--user"
        data-testid="aiCodeGeneratorUserAvatar"
        title={userInfo?.username ?? ""}
      >
        {KMainFunctions.getUserLettersToShow(userInfo)}
      </KuikaAvatar>
    );
  };

  /** Send arrow / loading indicator, shared by both composer states. */
  renderSendAffordance = (): ReactNode => {
    if (this.state.isLoading) {
      return (
        <span data-testid="aiCodeGeneratorLoadingSpinner">
          <SpinSvg className="kuika__ai-code-generator__input-suffix spin" />
        </span>
      );
    }
    return (
      <span data-testid="aiCodeGeneratorSendButton">
        <KuikaSvg
          type="arrows"
          name="arrow-up"
          width={16}
          height={16}
          pointer
          className="kuika__ai-code-generator__input-suffix"
          onClick={() => {
            this.formRef.current?.submit();
          }}
        />
      </span>
    );
  };

  inputPrefix = (): ReactNode => {
    const { type, tables } = this.props;

    if (
      (type === EAICodeGeneratorType.SQL ||
        type === EAICodeGeneratorType.Oracle ||
        type === EAICodeGeneratorType.Postgre) &&
      tables
    ) {
      return (
        <span className="kuika__ai-code-generator__form-container__tables-wrapper">
          <Form.Item
            name="dataTable"
            className="kuika__ai-code-generator__form-container__tables"
            rules={[{ required: true, message: "" }]}
          >
            <KuikaSelectbox
              size="sm"
              searchable
              placeholder="Choose Table"
              prefix={<KuikaSvg name="table" width={14} height={14} />}
              suffixIcon={<KuikaSvg type="arrows" name="chevron-down" width={14} height={14} />}
              onChange={this.handleTableSelected}
              data-testid="aiCodeGeneratorTableSelect"
              options={tables.map((table) => ({
                value: table,
                searchLabel: table,
                // The name is wrapped so the option keeps the handle the E2E suite picks it by:
                // the kit takes options as data and has nowhere else to put one.
                label: <span data-testid={`aiCodeGeneratorTableOption-${table}`}>{table}</span>
              }))}
            />
          </Form.Item>
        </span>
      );
    }

    return undefined;
  };

  render() {
    let inputPlaceholder = "Type here...";
    let initMessage =
      "I'm ready to help you with your custom C# actions. Let me show you how I can help you with a few examples.";

    if (this.props.type === EAICodeGeneratorType.TypeScript) {
      initMessage =
        "I'm ready to help you with your custom TypeScript actions. Let me show you how I can help you with a few examples.";
    }

    if (this.props.type === EAICodeGeneratorType.Python) {
      initMessage =
        "I'm ready to help you with your custom Python actions. Let me show you how I can help you with a few examples.";
    }

    if (this.props.type === EAICodeGeneratorType.Java) {
      initMessage =
        "I'm ready to help you with your custom Java actions. Let me show you how I can help you with a few examples.";
    }

    if (this.props.type === EAICodeGeneratorType.Go) {
      initMessage =
        "I'm ready to help you with your custom Go actions. Let me show you how I can help you with a few examples.";
    }

    if (this.props.type === EAICodeGeneratorType.Rust) {
      initMessage =
        "I'm ready to help you with your custom Rust actions. Let me show you how I can help you with a few examples.";
    }

    if (this.props.type === EAICodeGeneratorType.SQL) {
      inputPlaceholder = "Write a SQL query to fetch all records from this table";
      initMessage =
        "I'm ready to help you with your SQL queries. Let me show you how I can help you with a few examples.";
    }

    if (this.props.type === EAICodeGeneratorType.Oracle) {
      inputPlaceholder = "Write a SQL query to fetch all records from this table";
      initMessage =
        "I'm ready to help you with your Oracle queries. Let me show you how I can help you with a few examples.";
    }

    const hasSuggestions: boolean = (this.props.suggestions?.length ?? 0) > 0;
    const tablePrefix: ReactNode = this.inputPrefix();
    const needsTableSelection: boolean = !!tablePrefix && !this.state.selectedTable;
    // The example the user picked shows through as the placeholder until it can be sent.
    const promptPlaceholder: string = this.state.pendingSuggestion ?? inputPlaceholder;

    return (
      <div className="kuika__ai-code-generator__container" data-testid="aiCodeGenerator">
        <div
          className="kuika__ai-code-generator__messages-container"
          data-testid="aiCodeGeneratorMessages"
          ref={this.messagesContainerRef}
        >
          {hasSuggestions
            ? this.state.messages.length === 0 && this.renderEmptyState()
            : this.renderMessage({ content: initMessage, role: EOpenAIUserRole.Assistant })}

          {this.state.messages.map((message) => this.renderMessage(message))}
        </div>
        <div className="kuika__ai-code-generator__form-container">
          <Form ref={this.formRef} onFinish={this.handleSendCodeGenerateRequest}>
            {needsTableSelection ? (
              <div className="kuika__ai-code-generator__composer" data-testid="aiCodeGeneratorTableHint">
                <span className="kuika__ai-code-generator__composer__hint">
                  {this.state.pendingSuggestion ??
                    "Please choose the database table you want to create a select query for."}
                </span>
                <div className="kuika__ai-code-generator__composer__row">
                  {tablePrefix}
                  {this.renderSendAffordance()}
                </div>
              </div>
            ) : (
              <Form.Item
                name="userMessage"
                rules={[{ required: true, message: "Please type message to generate code." }]}
                noStyle
              >
                <KuikaInput
                  leftIcon={tablePrefix}
                  rightIcon={this.renderSendAffordance()}
                  onKeyDown={this.handleInputOnEnter}
                  placeholder={promptPlaceholder}
                  data-testid="aiCodeGeneratorPromptInput"
                />
              </Form.Item>
            )}
          </Form>
        </div>
      </div>
    );
  }
}
